import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  HELP_EMAIL,
  LoggingService,
  NavLink,
  PartnerAccountRoles,
  PartnerUserService,
  PartnerVenueDetails,
  PRODUCT_NAME,
  RedirectService,
  SessionService,
  SessionUser,
  UserRoles,
} from "src/app/core";
import { AccountUpgradeService } from "src/app/features/account/services";
import { ReferralRewardsUiService } from "src/app/features/referral-rewards";
import { VenuesService } from "src/app/features/venues";

const Workflow = "Protected";
const WorkflowSource = "UserIconMenu";

@Component({
  selector: "app-user-icon-menu",
  templateUrl: "./user-icon-menu.component.html",
  styleUrls: ["./user-icon-menu.component.scss"],
  providers: [ReferralRewardsUiService],
  standalone: false,
})
export class UserIconMenuComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject();

  @Input() user: SessionUser;
  @Input() includeExtras: boolean;

  showSupport: boolean;
  showDevelopers: boolean;
  showReferralRewards: boolean;
  showPartnerHelp: boolean;
  partnerVenues: [PartnerVenueDetails, string][];
  isUpgradable: boolean;
  isClientUser: boolean;
  shadowMode: boolean;
  isAdminUser: boolean;

  readonly PRODUCT_NAME = PRODUCT_NAME;
  readonly HELP_EMAIL = HELP_EMAIL;

  constructor(
    private _sessionService: SessionService,
    private _partnerUserService: PartnerUserService,
    private _router: Router,
    private _redirectService: RedirectService,
    private _venuesService: VenuesService,
    private _accountUpgradeService: AccountUpgradeService,
    private _referralUiService: ReferralRewardsUiService,
    private _loggingService: LoggingService,
  ) {}

  ngOnInit() {
    this.isUpgradable = !!this._accountUpgradeService.getPartnerUpgradeAccountType();
    this.isClientUser = this._sessionService.isClientUser();
    this.isAdminUser =
      this._partnerUserService.isCurrentPartnerActiveStatus() &&
      !this._partnerUserService.canSelfUpgrade() &&
      this._partnerUserService.getCurrentPartnerUser()?.role === PartnerAccountRoles.Admin;
    this._sessionService.shadowMode$.pipe(takeUntil(this._destroyed$)).subscribe(shadowMode => (this.shadowMode = shadowMode));
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  logout() {
    this._loggingService.logWorkflow(
      "User icon menu logout click",
      "UserIconMenuComponent.logout",
      Workflow,
      "LogoutClick",
      WorkflowSource,
    );

    this._router.navigate(["/logout"]);
  }

  onMenuTrigger(): void {
    this._loggingService.logWorkflow(
      "User icon menu opened",
      "UserIconMenuComponent.onMenuTrigger",
      Workflow,
      "MenuOpened",
      WorkflowSource,
    );

    this.showSupport = this._sessionService.hasUserRole(UserRoles.Support);
    this.showDevelopers = this._sessionService.hasUserRole(UserRoles.ExternalDeveloper);
    this.showReferralRewards = this._partnerUserService.getCurrentPartnerUser()?.canReceiveReferralRewards;
    this.showPartnerHelp = !!this._partnerUserService.getCurrentPartnerUser();
    this.partnerVenues = this._partnerUserService
      .getCurrentPartnerUser()
      ?.venues.map(i => [i, this._venuesService.getPublicUrl(i.venueName, i.venueId)]);
  }

  onMenuClosed(): void {
    this._loggingService.logWorkflow("User icon menu closed", "UserIconMenuComponent.onMenuClosed", Workflow, "MenuClosed", WorkflowSource);
  }

  onInitialsClick(): void {
    this._loggingService.logWorkflow(
      "User icon menu initials click",
      "UserIconMenuComponent.onInitialsClick",
      Workflow,
      "UserInitialsClick",
      WorkflowSource,
    );
  }

  onAdditionalNavClick(additionalNav: NavLink): void {
    this._loggingService.logWorkflow(
      "User icon menu additional nav click",
      "UserIconMenuComponent.onAdditionalNavClick",
      Workflow,
      "AdditionalNavClick",
      WorkflowSource,
      {
        NavLabel: additionalNav.label,
      },
    );
  }

  onPartnerVenueNavClick(partnerVenue: [PartnerVenueDetails, string]): void {
    this._loggingService.logWorkflow(
      "User icon menu partner venue nav click",
      "UserIconMenuComponent.onPartnerVenueNavClick",
      Workflow,
      "PartnerVenueNavClick",
      WorkflowSource,
      {
        NavPartnerVenue: partnerVenue[0].venueName,
      },
    );
  }

  onAccountSettingsNavClick(): void {
    this._loggingService.logWorkflow(
      "User icon menu account settings nav click",
      "UserIconMenuComponent.onAccountSettingsNavClick",
      Workflow,
      "AccountSettingsNavClick",
      WorkflowSource,
    );
  }

  onProdSupportNavClick(): void {
    this._loggingService.logWorkflow(
      "User icon menu prod support nav click",
      "UserIconMenuComponent.onProdSupportNavClick",
      Workflow,
      "ProdSupportNavClick",
      WorkflowSource,
    );
  }

  onApiNavClick(): void {
    this._loggingService.logWorkflow(
      "User icon menu api nav click",
      "UserIconMenuComponent.onApiNavClick",
      Workflow,
      "ApiNavClick",
      WorkflowSource,
    );
  }

  onReferralRewardsClick(): void {
    this._referralUiService.openReferralRewardsDialog("UserIconMenu");
    LoggingService.log("PartnerReferAFriendDialogComponent - Dialog opened");
  }

  onPartnerHelpClick(): void {
    this._loggingService.logWorkflow(
      "User icon menu help center click",
      "UserIconMenuComponent.onPartnerHelpClick",
      Workflow,
      "HelpCenterClick",
      WorkflowSource,
    );
    this._redirectService.goToPartnerPortalHelpGuide();
  }

  logoutLabel(): string {
    return this.shadowMode ? "Shadow Logout" : "Log out";
  }
}
